import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { bookingAPI } from '../services/api';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import logoImage from '../assets/images/logo.png';
import '../styles/invoice.css';

const Invoice = () => {
    const { bookingId } = useParams();
    const location = useLocation();
    const invoiceRef = useRef(null);
    const [booking, setBooking] = useState(location.state?.booking || null);
    const [loading, setLoading] = useState(!location.state?.booking);
    const [error, setError] = useState('');
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const bookingType = useMemo(() => {
        const queryType = new URLSearchParams(location.search).get('type');
        return queryType || location.state?.type || location.state?.booking?.type || 'package';
    }, [location.search, location.state]);

    const normalizeBooking = useCallback((payload) => {
        if (!payload) return null;

        const source = payload.data || payload;

        if (bookingType === 'camping') {
            return {
                type: 'camping',
                booking_id: source.booking_id || source.bookingId || bookingId,
                package_title: source.site_name || source.siteName || 'Camping Stay',
                customer_name: source.customer_name || source.customerName || 'Guest Traveler',
                customer_email: source.customer_email || source.customerEmail || '',
                customer_phone: source.customer_phone || source.customerPhone || '',
                adults: Number(source.guests || source.adults || 0),
                children: 0,
                travel_date: source.check_in || source.checkIn || '',
                total_amount: Number(source.total_amount || source.totalAmount || 0),
                original_amount: Number(source.original_amount || source.originalAmount || source.total_amount || source.totalAmount || 0),
                advance_amount: Number(source.advance_amount || source.advanceAmount || source.total_amount || source.totalAmount || 0),
                base_package_amount: Number(source.base_package_amount || source.basePackageAmount || 0),
                addons_amount: Number(source.addons_amount || source.addonsAmount || 0),
                coupon_code: source.coupon_code || source.couponCode || '',
                coupon_discount: Number(source.coupon_discount || source.couponDiscount || 0),
                addon_services: source.addon_services || source.selected_addons || [],
                payment_method: source.payment_method || source.paymentMethod || 'full',
                payment_status: source.payment_status || source.paymentStatus || 'pending',
                booking_status: source.booking_status || source.bookingStatus || 'pending',
                transaction_id: source.transaction_id || source.transactionId || '',
                special_requests: source.special_requests || source.specialRequests || '',
                created_at: source.created_at || source.createdAt || '',
                destinations: source.location || source.location_name || source.location_address || '',
                duration: source.total_nights ? `${source.total_nights} Night${Number(source.total_nights) === 1 ? '' : 's'}` : (source.nights ? `${source.nights} Night${Number(source.nights) === 1 ? '' : 's'}` : ''),
                package_id: source.site_id || source.siteId || '',
                check_in: source.check_in || source.checkIn || '',
                check_out: source.check_out || source.checkOut || '',
                guests: Number(source.guests || 0),
                total_nights: Number(source.total_nights || source.nights || 0),
                site_name: source.site_name || source.siteName || 'Camping Stay',
                location_name: source.location_name || '',
                location_address: source.location_address || '',
                site_type: source.site_type || ''
            };
        }

        return {
            type: 'package',
            booking_id: source.booking_id || source.bookingId || bookingId,
            package_title: source.package_title || source.packageTitle || 'Travel Package',
            customer_name: source.customer_name || source.customerName || 'Guest Traveler',
            customer_email: source.customer_email || source.customerEmail || '',
            customer_phone: source.customer_phone || source.customerPhone || '',
            adults: Number(source.adults || 0),
            children: Number(source.children || 0),
            travel_date: source.travel_date || source.travelDate || '',
            total_amount: Number(source.total_amount || source.totalAmount || 0),
            original_amount: Number(source.original_amount || source.originalAmount || source.total_amount || source.totalAmount || 0),
            advance_amount: Number(source.advance_amount || source.advanceAmount || 0),
            base_package_amount: Number(source.base_package_amount || source.basePackageAmount || 0),
            addons_amount: Number(source.addons_amount || source.addonsAmount || 0),
            coupon_code: source.coupon_code || source.couponCode || '',
            coupon_discount: Number(source.coupon_discount || source.couponDiscount || 0),
            addon_services: source.addon_services || source.selected_addons || [],
            payment_method: source.payment_method || source.paymentMethod || 'full',
            payment_status: source.payment_status || source.paymentStatus || 'pending',
            booking_status: source.booking_status || source.bookingStatus || 'pending',
            transaction_id: source.transaction_id || source.transactionId || '',
            special_requests: source.special_requests || source.specialRequests || '',
            created_at: source.created_at || source.createdAt || '',
            destinations: source.destinations || '',
            duration: source.duration || '',
            package_id: source.package_id || source.packageId || ''
        };
    }, [bookingId, bookingType]);

    useEffect(() => {
        if (location.state?.booking) {
            setBooking(normalizeBooking(location.state.booking));
            setLoading(false);
            return;
        }

        const loadInvoiceBooking = async () => {
            try {
                setLoading(true);
                const response = bookingType === 'camping'
                    ? await axios.get(buildApiUrl(`/camping/booking/${bookingId}`))
                    : await bookingAPI.trackBooking(bookingId);
                const normalized = normalizeBooking(response.data);

                if (!normalized) {
                    throw new Error('Invoice details not found');
                }

                setBooking(normalized);
                setError('');
            } catch (err) {
                console.error('Invoice fetch error:', err);
                setError('Unable to load invoice details right now.');
            } finally {
                setLoading(false);
            }
        };

        loadInvoiceBooking();
    }, [bookingId, bookingType, location.state, normalizeBooking]);

    useEffect(() => {
        document.title = booking ? `Invoice ${booking.booking_id} | RedGoTrip` : 'Invoice | RedGoTrip';
    }, [booking]);

    const shouldAutoPrint = useMemo(() => {
        return new URLSearchParams(location.search).get('download') === '1';
    }, [location.search]);

    useEffect(() => {
        if (!booking || !shouldAutoPrint) return;

        const timer = window.setTimeout(() => {
            window.print();
        }, 500);

        return () => window.clearTimeout(timer);
    }, [booking, shouldAutoPrint]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(Number(amount || 0));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';

        const parsed = new Date(dateString);
        if (Number.isNaN(parsed.getTime())) {
            return dateString;
        }

        return parsed.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const invoiceMeta = useMemo(() => {
        if (!booking) return null;

        const issueDate = booking.created_at ? formatDate(booking.created_at) : formatDate(new Date());
        const paidAmount = booking.advance_amount > 0 ? booking.advance_amount : booking.total_amount;
        const balanceDue = Math.max(Number(booking.total_amount || 0) - Number(paidAmount || 0), 0);
        const travellerCount = bookingType === 'camping'
            ? `${booking.guests || booking.adults || 0} Guest${Number(booking.guests || booking.adults || 0) === 1 ? '' : 's'}`
            : `${booking.adults} Adult${booking.adults === 1 ? '' : 's'}${booking.children > 0 ? `, ${booking.children} Child${booking.children === 1 ? '' : 'ren'}` : ''}`;

        return {
            invoiceNumber: `INV-${booking.booking_id}`,
            issueDate,
            paidAmount,
            balanceDue,
            travellerCount,
            paymentMethodLabel: String(booking.payment_method || 'full').replace(/^\w/, (c) => c.toUpperCase()),
            paymentStatusLabel: String(booking.payment_status || 'pending').replace(/^\w/, (c) => c.toUpperCase()),
            bookingStatusLabel: String(booking.booking_status || 'pending').replace(/^\w/, (c) => c.toUpperCase())
        };
    }, [booking, bookingType]);

    const loadScript = useCallback((src) => new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
            if (existing.dataset.loaded === 'true') {
                resolve();
                return;
            }

            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => {
            script.dataset.loaded = 'true';
            resolve();
        };
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.body.appendChild(script);
    }), []);

    const handleDownloadPdf = useCallback(async () => {
        if (!invoiceRef.current || !booking) return;

        try {
            setDownloadingPdf(true);

            await Promise.all([
                loadScript('https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js'),
                loadScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js')
            ]);

            const html2canvas = window.html2canvas;
            const jsPDF = window.jspdf?.jsPDF;

            if (!html2canvas || !jsPDF) {
                throw new Error('PDF libraries are unavailable');
            }

            const canvas = await html2canvas(invoiceRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false
            });

            const imageData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pageWidth - margin * 2;
            const contentHeight = (canvas.height * contentWidth) / canvas.width;

            let remainingHeight = contentHeight;
            let positionY = margin;

            pdf.addImage(imageData, 'PNG', margin, positionY, contentWidth, contentHeight, undefined, 'FAST');
            remainingHeight -= pageHeight - margin * 2;

            while (remainingHeight > 0) {
                positionY = remainingHeight - contentHeight + margin;
                pdf.addPage();
                pdf.addImage(imageData, 'PNG', margin, positionY, contentWidth, contentHeight, undefined, 'FAST');
                remainingHeight -= pageHeight - margin * 2;
            }

            pdf.save(`invoice-${booking.booking_id}.pdf`);
        } catch (downloadError) {
            console.error('PDF download error:', downloadError);
            window.alert('Unable to generate PDF right now. Please check internet connection and try again.');
        } finally {
            setDownloadingPdf(false);
        }
    }, [booking, loadScript]);

    if (loading) {
        return (
            <div className="invoice-page invoice-loading">
                <div className="invoice-loader"></div>
                <p>Preparing your invoice...</p>
            </div>
        );
    }

    if (error || !booking || !invoiceMeta) {
        return (
            <div className="invoice-page invoice-error-state">
                <div className="invoice-error-card">
                    <h1>Invoice Unavailable</h1>
                    <p>{error || 'We could not find invoice details for this booking.'}</p>
                    <Link to={`/booking/track/${bookingId}`} className="invoice-back-link">
                        Back to Booking
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="invoice-page">
            <div className="invoice-shell">
                <div className="invoice-toolbar no-print">
                    <Link
                        to={bookingType === 'camping' ? `/camping-booking-success/${booking.booking_id}` : `/booking-success/${booking.booking_id}`}
                        state={{ booking, type: bookingType }}
                        className="toolbar-link"
                    >
                        <i className="fas fa-arrow-left"></i>
                        Back
                    </Link>
                    <div className="toolbar-actions">
                        <button type="button" className="toolbar-btn secondary" onClick={() => window.print()}>
                            <i className="fas fa-print"></i>
                            Print
                        </button>
                        <button type="button" className="toolbar-btn primary" onClick={handleDownloadPdf} disabled={downloadingPdf}>
                            <i className={`fas ${downloadingPdf ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                            {downloadingPdf ? 'Generating PDF...' : 'Download PDF'}
                        </button>
                    </div>
                </div>

                <div className="invoice-card" id="booking-invoice" ref={invoiceRef}>
                    <div className="invoice-brand">
                        <div>
                            <span className="brand-kicker">RedGoTrip Travel Invoice</span>
                            <h1>Tax Invoice</h1>
                            <p>Issued for your confirmed {bookingType === 'camping' ? 'camping stay' : 'travel booking'}.</p>
                        </div>
                        <div className="brand-badge">
                            <img src={logoImage} alt="RedGoTrip" className="invoice-brand-logo" />
                            <small>Travel Services</small>
                        </div>
                    </div>

                    <div className="invoice-top-grid">
                        <div className="invoice-panel">
                            <span className="panel-label">Billed To</span>
                            <h3>{booking.customer_name}</h3>
                            <p>{booking.customer_email || 'Email available in booking confirmation'}</p>
                            <p>{booking.customer_phone || 'Phone available in booking confirmation'}</p>
                        </div>
                        <div className="invoice-panel invoice-panel-meta">
                            <div>
                                <span className="panel-label">Invoice No.</span>
                                <strong>{invoiceMeta.invoiceNumber}</strong>
                            </div>
                            <div>
                                <span className="panel-label">Booking ID</span>
                                <strong>{booking.booking_id}</strong>
                            </div>
                            <div>
                                <span className="panel-label">Issue Date</span>
                                <strong>{invoiceMeta.issueDate}</strong>
                            </div>
                        </div>
                    </div>

                    <div className="invoice-trip-summary">
                        <div className="summary-item">
                            <span>{bookingType === 'camping' ? 'Site' : 'Package'}</span>
                            <strong>{bookingType === 'camping' ? (booking.site_name || booking.package_title) : booking.package_title}</strong>
                        </div>
                        <div className="summary-item">
                            <span>{bookingType === 'camping' ? 'Check-in' : 'Travel Date'}</span>
                            <strong>{formatDate(bookingType === 'camping' ? booking.check_in : booking.travel_date)}</strong>
                        </div>
                        <div className="summary-item">
                            <span>{bookingType === 'camping' ? 'Guests' : 'Travelers'}</span>
                            <strong>{invoiceMeta.travellerCount}</strong>
                        </div>
                        <div className="summary-item">
                            <span>{bookingType === 'camping' ? 'Stay Length' : 'Duration'}</span>
                            <strong>{booking.duration || 'As per itinerary'}</strong>
                        </div>
                    </div>

                    <div className="invoice-table-wrap">
                        <table className="invoice-table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th>Payment Method</th>
                                    <th>Payment Status</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        <div className="invoice-service">
                                            <strong>{booking.package_title}</strong>
                                            <span>{bookingType === 'camping' ? (booking.destinations || 'Camping stay booking') : (booking.destinations || 'Holiday package booking')}</span>
                                        </div>
                                    </td>
                                    <td>{invoiceMeta.paymentMethodLabel}</td>
                                    <td>
                                        <span className={`invoice-status ${String(booking.payment_status).toLowerCase()}`}>
                                            {invoiceMeta.paymentStatusLabel}
                                        </span>
                                    </td>
                                    <td>{formatCurrency(booking.base_package_amount || booking.total_amount)}</td>
                                </tr>
                                {Array.isArray(booking.addon_services) && booking.addon_services.map((addon, index) => (
                                    <tr key={`${addon.addon_service_id || index}-${index}`}>
                                        <td>
                                            <div className="invoice-service">
                                                <strong>{addon.title_snapshot || addon.title || 'Add-on Service'}</strong>
                                                <span>{String(addon.price_type || 'fixed').replace('_', ' ')} • Qty {Number(addon.quantity || 1)}</span>
                                            </div>
                                        </td>
                                        <td>{invoiceMeta.paymentMethodLabel}</td>
                                        <td>
                                            <span className={`invoice-status ${String(booking.payment_status).toLowerCase()}`}>
                                                {invoiceMeta.paymentStatusLabel}
                                            </span>
                                        </td>
                                        <td>{formatCurrency(addon.total_price || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="invoice-bottom-grid">
                        <div className="invoice-notes">
                            <h3>Booking Notes</h3>
                            <ul>
                                <li>Booking status: {invoiceMeta.bookingStatusLabel}</li>
                                <li>Transaction ID: {booking.transaction_id || 'Will be shared after gateway confirmation'}</li>
                                <li>Special requests: {booking.special_requests || 'No additional requests provided'}</li>
                                {bookingType === 'camping' && booking.check_out ? <li>Check-out: {formatDate(booking.check_out)}</li> : null}
                            </ul>
                        </div>

                        <div className="invoice-totals">
                            {Number(booking.base_package_amount || 0) > 0 && (
                                <div className="total-row">
                                    <span>Base Package</span>
                                    <strong>{formatCurrency(booking.base_package_amount)}</strong>
                                </div>
                            )}
                            {Number(booking.addons_amount || 0) > 0 && (
                                <div className="total-row">
                                    <span>Add-on Services</span>
                                    <strong>{formatCurrency(booking.addons_amount)}</strong>
                                </div>
                            )}
                            <div className="total-row">
                                <span>Original Total</span>
                                <strong>{formatCurrency(booking.original_amount || booking.total_amount)}</strong>
                            </div>
                            {booking.coupon_code && Number(booking.coupon_discount || 0) > 0 && (
                                <div className="total-row">
                                    <span>Coupon ({booking.coupon_code})</span>
                                    <strong>-{formatCurrency(booking.coupon_discount)}</strong>
                                </div>
                            )}
                            <div className="total-row">
                                <span>Amount Paid</span>
                                <strong>{formatCurrency(invoiceMeta.paidAmount)}</strong>
                            </div>
                            <div className="total-row">
                                <span>Balance Due</span>
                                <strong>{formatCurrency(invoiceMeta.balanceDue)}</strong>
                            </div>
                            <div className="total-row grand">
                                <span>Net Booking Value</span>
                                <strong>{formatCurrency(booking.total_amount)}</strong>
                            </div>
                        </div>
                    </div>

                    <div className="invoice-footer">
                        <div>
                            <strong>Support</strong>
                            <p>support@redgotrips.com</p>
                            <p>+91 9335444047</p>
                        </div>
                        <div>
                            <strong>Address</strong>
                            <p>388 B H S, Shantipuram, Allahabad, Uttar Pradesh - 211006</p>
                            <p>India</p>
                        </div>
                        <div className="invoice-footer-note">
                            <strong>Important</strong>
                            <p>This is a system-generated invoice and is valid without signature.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Invoice;
